import * as angular from 'angular';

import {
    MathUtils
} from 'src/app/shared/utils';

import {
    CategoriesVariations,
    ItemsVariations,
    ItemsVariationValues,
    SalesItems,
    SalesItemsVariations
} from 'tilby-models';

interface AddVariationsDialogParams {
    rowItem: SalesItems;
    variations: VariationsForm[];
}

type VariationsForm = (ItemsVariations | CategoriesVariations) & {
    variation_value_id?: number
    quantities?: Record<number, number>
    total_quantity?: number
};

class AddVariationsDialogController {
    name: string;
    rowItem: SalesItems;
    variations: VariationsForm[] = [];
    areVariationsSatisfied: boolean = false;

    static $inject = [
        '$scope',
        'params',
        '$mdDialog',
        '$filter',
        '$translate',
        '$rootScope',
    ];

    constructor(
        $scope: any,
        params: AddVariationsDialogParams,
        private $mdDialog: any,
        private $filter: any,
        $translate: any,
        private $rootScope: any,
    ) {
        $scope.vm = this; // Bind vm to this for template access

        this.variations = structuredClone(params.variations || []);
        this.rowItem = params.rowItem;
        this.name = this.rowItem.name || $translate.instant('KIOSK.ADD_VARIATIONS_DIALOG.ADD_VARIATIONS');

        // Add 'no choice' option for non-required variations
        for(const variation of this.variations) {
            const variationValues = variation.variation_values || [];

            // Sort variation values by index
            variationValues.sort((a, b) => (a.index || 0) - (b.index || 0));

            if (!variation.required && !variation.max_quantity) {
                variationValues.unshift({
                    value: $translate.instant('KIOSK.ADD_VARIATIONS_DIALOG.NONE'),
                    id: undefined
                });
            }

            variation.variation_values = variationValues;

            if(variation.max_quantity! >= 1) {
                variation.quantities = {};
                variation.total_quantity = 0;
            }
        }

        this.areVariationsSatisfied = this.checkMandatoryVariations();

        $scope.$on('kiosk:standby', () => {
            this.$mdDialog.cancel();
        });
    }

    getPriceDifferenceValue(variationValue: ItemsVariationValues) {
        if (variationValue.price_difference == null) {
            return variationValue.price_difference;
        }

        switch (variationValue.price_difference_type) {
            case 'perc':
                return MathUtils.round(this.rowItem.price * (variationValue.price_difference / 100));
            case 'value':
            default:
                return variationValue.price_difference;
        }
    }

    /**
     * Updates the total quantity of a variation by summing up the quantities of its variation values.
     * @param {VariationsForm} variation The variation whose total quantity needs to be updated.
     */
    updateVariationTotalQuantity(variation: VariationsForm) {
        if(!variation.quantities) {
            return;
        }

        variation.total_quantity = Object.values(variation.quantities || {}).reduce((a, b) => a + b, 0);
    }

    private changeVariationQuantity(variation: VariationsForm, variationValue: ItemsVariationValues, quantityDelta: number) {
        // Ensure variation and variation value IDs exist
        if (!variation.id || !variationValue.id) {
            return;
        }

        const variationQuantities = variation.quantities || {};
        const currentQuantity = variationQuantities[variationValue.id] || 0;

        // Update the quantity for the variation value
        variationQuantities[variationValue.id] = currentQuantity + quantityDelta;

        variation.quantities = variationQuantities;
        this.updateVariationTotalQuantity(variation);

        this.areVariationsSatisfied = this.checkMandatoryVariations();
    }

    /**
     * Increases the quantity of a specific variation value.
     * @param {VariationsForm} variation The variation object.
     * @param {ItemsVariationValues} variationValue The specific variation value to increase.
     */
    increaseVariationQuantity(variation: VariationsForm, variationValue: ItemsVariationValues) {
        this.changeVariationQuantity(variation, variationValue, + 1);
    }

    /**
     * Decreases the quantity of a specific variation value.
     * @param {VariationsForm} variation The variation object.
     * @param {ItemsVariationValues} variationValue The specific variation value to decrease.
     */
    decreaseVariationQuantity(variation: VariationsForm, variationValue: ItemsVariationValues) {
        this.changeVariationQuantity(variation, variationValue, -1);
    }

    onVariationChange() {
        this.areVariationsSatisfied = this.checkMandatoryVariations();
    }

    checkMandatoryVariations() {
        for(const variation of this.variations) {
            if(variation.max_quantity && variation.max_quantity! >= 1) {
                if(variation.min_quantity && variation.total_quantity! < variation.min_quantity) {
                    return false;
                }

                if(variation.total_quantity! > variation.max_quantity) {
                    return false;
                }
            } else if(variation.required && !variation.variation_value_id) {
                return false;
            }
        }

        return true;
    }

        /**
     * Calculates and returns the price difference for a variation value as a formatted string.
     * @param {ItemsVariationValues} variationValue The variation value for which to get the price difference.
     * @returns {string} The formatted price difference string (e.g., "+$5.00", "-$2.50", or empty if no difference).
     */
    getPriceDifferenceValueString(variationValue: ItemsVariationValues) {
        const value = this.getPriceDifferenceValue(variationValue);

        if (value == null) {
            return '';
        }

        return value > 0 ? `+${this.$filter('sclCurrency')(value)}` : this.$filter('sclCurrency')(value);
    }

    enableTimeouts(): void {
        (this.$rootScope as any).enableTimeouts(); // Assuming enableTimeouts is a method on $rootScope
    }

    cancel(): void {
        this.$mdDialog.cancel();
    }

    private parseNewVariations() {
        const newVariations: SalesItemsVariations[] = [];

        for (const variation of this.variations) {
            if(variation.max_quantity! >= 1) {
                for(const [valueId, quantity] of Object.entries(variation.quantities || {})) {
                    if(!quantity) {
                        continue;
                    }

                    const variationValueId = +valueId;
                    const found = variation.variation_values?.find((element) => element.id === variationValueId);

                    if(!found) {
                        continue;
                    }

                    newVariations.push({
                        name: variation.name,
                        value: found.value!,
                        price_difference: this.getPriceDifferenceValue(found),
                        linked_item_uuid: found.linked_item_uuid,
                        linked_item_sku: found.linked_item_sku,
                        quantity: quantity,
                        variation_id: variation.id!,
                        variation_value_id: variationValueId
                    });
                }
            } else if(variation.variation_value_id) {
                const found = variation.variation_values?.find((element) => element.id === variation.variation_value_id);
    
                if (!found) {
                    continue;
                }

                newVariations.push({
                    name: variation.name,
                    value: found.value!,
                    price_difference: this.getPriceDifferenceValue(found),
                    linked_item_uuid: found.linked_item_uuid,
                    linked_item_sku: found.linked_item_sku,
                    variation_id: variation.id!,
                    variation_value_id: variation.variation_value_id!
                });
            }
        }

        return newVariations;
    }

    proceed(): void {
        this.$mdDialog.hide({ variations: this.parseNewVariations() });
    }
}

export class AddVariationsDialogService {
    static $inject = [
        '$mdDialog'
    ];

    constructor(
        private $mdDialog: any
    ) {
    }

    show(params: AddVariationsDialogParams): angular.IPromise<any> {
        return this.$mdDialog.show({
            controller: AddVariationsDialogController,
            controllerAs: 'vm', // Use controllerAs to bind to 'vm' in the template
            template: require('./add-variations.html'),
            locals: {
                params: params
            },
        });
    }
}

angular.module('kiosk').service('addVariationsDialog', AddVariationsDialogService);